import type { ReactNode } from 'react';
import { Radio, RadioGroup } from '@headlessui/react';
import { cn } from '@/lib/cn';

interface Option<T extends string> {
  value: T;
  label: ReactNode;
  /** Language of the label, for screen readers (native language names). */
  lang?: string;
}

/** Pill switcher (English / Français, Quantities / Values, People / Roles). */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  label,
  className,
  size = 'md',
}: {
  value: T;
  onChange: (value: T) => void;
  options: Array<Option<T>>;
  label: string;
  className?: string;
  size?: 'md' | 'lg';
}) {
  return (
    <RadioGroup
      value={value}
      onChange={onChange}
      aria-label={label}
      className={cn('inline-flex rounded-control bg-neutral-soft p-1', className)}
    >
      {options.map((option) => (
        <Radio
          key={option.value}
          value={option.value}
          lang={option.lang}
          className={cn(
            'flex flex-1 cursor-pointer items-center justify-center rounded-[8px] px-4 font-medium text-ink-2 whitespace-nowrap transition-colors',
            'data-checked:bg-surface data-checked:font-semibold data-checked:text-ink data-checked:shadow-sm',
            'data-focus:outline-2 data-focus:outline-primary',
            size === 'lg' ? 'h-12 text-[17px]' : 'h-9',
          )}
        >
          {option.label}
        </Radio>
      ))}
    </RadioGroup>
  );
}
